import React, { FC } from 'react';

import { EvFlexColumn, useSkipLinks } from '@evinced-private/ui-common';

import { MfaError } from 'src/apps/mfa/components/MfaError';
import { MfaSessionNotSyncedYetPopup } from 'src/apps/mfa/components/MfaSessionNotSyncedYetPopup';
import { useMfaSessionsQuery } from 'src/apps/mfa/hooks/useMfaSessionsQuery';
import { useMfaSummaryDataQuery } from 'src/apps/mfa/hooks/useMfaSummaryDataQuery';
import { MfaSessionsPageHeader } from 'src/apps/mfa/pages/sessions/sections/header/MfaSessionsHeader';
import { MfaSessionsSummary } from 'src/apps/mfa/pages/sessions/sections/summary/MfaSessionsSummary';
import { MfaSessionsTable } from 'src/apps/mfa/pages/sessions/sections/table/MfaSessionsTable';
import { MobileErrorType } from 'src/apps/mobile-common/components/mobile-error-message/MobileErrorMessage';
import { HorizontalDivider } from 'src/common/components/horizontal-divider/HorizontalDivider';
import { MFA_SESSIONS_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';

export const MFASessionsPage: FC = () => {
	const { getTableStateById, onFilterChange } = useTableState();
	const { filters, tableSort, paginationInfo } = getTableStateById(MFA_SESSIONS_TABLE_ID);

	const summaryDataQuery = useMfaSummaryDataQuery(filters);

	const sessionsQuery = useMfaSessionsQuery({
		page: paginationInfo.page,
		pageSize: paginationInfo.sizePerPage,
		sort: tableSort?.[0],
		filters
	});

	useSkipLinks(!summaryDataQuery.isLoading && !sessionsQuery.isLoading);

	const unfilteredTotal = sessionsQuery.data?.unfilteredTotal ?? 0;
	const showError = sessionsQuery?.isError || summaryDataQuery?.isError;
	const isNodataError =
		!sessionsQuery?.isLoading && !sessionsQuery?.isPreviousData && unfilteredTotal === 0;

	if (showError) {
		return <MfaError errorType={MobileErrorType.LOADING_ERROR} />;
	}

	if (isNodataError) {
		return (
			<>
				<MfaError errorType={MobileErrorType.NO_DATA} />
				<MfaSessionNotSyncedYetPopup />
			</>
		);
	}

	return (
		<EvFlexColumn padding="XLARGE" dataAutomationId="mfa-sessions-page">
			<MfaSessionsPageHeader filters={filters} onFilterChange={onFilterChange} />
			<HorizontalDivider />
			<MfaSessionsSummary
				data={summaryDataQuery.data}
				isLoading={summaryDataQuery.isLoading}
				selectedPeriodLabel={filters?.sessionsPeriod?.[0]?.label}
			/>
			<MfaSessionsTable sessionsQuery={sessionsQuery} unfilteredTotal={unfilteredTotal} />
			<MfaSessionNotSyncedYetPopup />
		</EvFlexColumn>
	);
};
